import { ROUTES } from "@/lib/routes";

/**
 * Round 6 (T2): every link to where the dbt repository is set, in one place, so the screens that
 * send a person there cannot drift. The repository, ref, project folder and git token are on the
 * Destination screen's "dbt repository" tab; "Open Destination" stays only where the
 * fix is on its Warehouse tab (the Snowflake account, the destination type, the sign-in).
 */

/** The dbt repository tab. */
export const DBT_SETTINGS_PATH = `${ROUTES.settingsDestination}?tab=dbt`;

/** The Warehouse tab (the page's first tab). */
export const WAREHOUSE_SETTINGS_PATH = ROUTES.settingsDestination;

/** A link to the dbt tab from a message or a checklist. */
export const OPEN_DBT_SETTINGS_LABEL = "Open dbt repository settings";

/** The Details pane's button, by what the person may do there (`dbt_jobs.write`). */
export const EDIT_DBT_SETTINGS_LABEL = "Edit dbt repository settings";
export const VIEW_DBT_SETTINGS_LABEL = "View dbt repository settings";

/** A link to the Warehouse tab. */
export const OPEN_WAREHOUSE_SETTINGS_LABEL = "Open Destination";

/** Where the dbt repository is set, in the words of the navigation. */
export const DBT_SETTINGS_PLACE = "Settings > Destination > dbt repository";

/** Where the warehouse is set, in the words of the navigation. */
export const WAREHOUSE_SETTINGS_PLACE = "Settings > Destination > Warehouse";

/**
 * #1106: the Destination tabs are Instance admin and above (`instance.binding.read`). A reader
 * without the key gets no link there, and reads who acts instead. The dbt repository is an
 * instance admin's to set. The warehouse is a platform admin's, reached through the instance admin.
 */
export const ASK_ADMIN_DBT_REPOSITORY = "Ask an instance admin to set the dbt repository.";
export const ASK_ADMIN_DESTINATION = "Ask an instance admin about the destination.";

/**
 * #1306: the dbt repository's address is Instance admin and above too. A reader below that is told
 * the repository is set (the graph's `repoUrlWithheld`), never "Not set", and who to ask.
 */
export const ASK_ADMIN_ABOUT_DBT_REPOSITORY = "Ask an instance admin about the dbt repository.";
export const DBT_REPOSITORY_WITHHELD = `Set. ${ASK_ADMIN_ABOUT_DBT_REPOSITORY}`;

/**
 * #1362: the branch, project folder and the target's warehouse are Instance
 * admin and above too (the graph's `bundle.detailsWithheld`). A reader below that is told the
 * value is set and who to ask, never "Not set" and never the value. The target's database and
 * schema stay every reader's, as on Your data (ADR 0011). The warehouse note carries no "Set":
 * a withheld warehouse may be unset.
 */
export const BUNDLE_DETAIL_WITHHELD = `Set. ${ASK_ADMIN_ABOUT_DBT_REPOSITORY}`;

/**
 * #1306: the destination's credential rows are Instance admin and above. A reader below that whose
 * destination needs attention reads this on the Overview's Credentials card, then who to ask. It
 * says how many, never which ones, so the card's heading counts what an admin's counts.
 */
export function destinationCredentialsWithheld(count: number): string {
  return count === 1
    ? "A destination credential needs attention."
    : `${count.toLocaleString()} destination credentials need attention.`;
}

/**
 * #1188, #1225: who sets the destination. Only a platform admin holds `instance.binding.write`.
 * An instance admin can read the destination and so is told to ask a platform admin. A role below
 * that cannot open Destination at all, so it is told the fact and to ask an instance admin to
 * pass it on. No sentence names a person who cannot do the work.
 */
export const ASK_ADMIN_SET_DESTINATION =
  "A platform admin sets the destination. Ask an instance admin.";
export const ASK_PLATFORM_ADMIN_SET_DESTINATION = "Ask a platform admin to set the destination.";

/** The sentence a reader without `instance.binding.write` gets for setting the destination. */
export function askToSetDestination(canReadDestination: boolean): string {
  return canReadDestination ? ASK_PLATFORM_ADMIN_SET_DESTINATION : ASK_ADMIN_SET_DESTINATION;
}
